export const THEMES = ['light', 'dark', 'system'] as const;

export type Theme = (typeof THEMES)[number];

export const DEFAULT_THEME: Theme = 'system';

/** Choix de l'utilisateur, relu à chaque visite. */
export const THEME_STORAGE_KEY = 'foundit.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

export const isTheme = (value: unknown): value is Theme => THEMES.includes(value as Theme);

const readStoredTheme = (): Theme => {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isTheme(stored) ? stored : DEFAULT_THEME;
    } catch {
        // Stockage indisponible (navigation privée stricte, cookies bloqués).
        return DEFAULT_THEME;
    }
};

const systemPrefersDark = () =>
    typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;

let currentTheme: Theme = readStoredTheme();

/** Classe "dark" (Tailwind, darkMode: 'class') et color-scheme (barres de défilement, champs). */
const applyTheme = (theme: Theme) => {
    const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
};

export const getTheme = () => currentTheme;

export const subscribeTheme = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const setTheme = (theme: Theme) => {
    currentTheme = theme;
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // Le thème reste appliqué pour la session même si on ne peut pas le mémoriser.
    }
    applyTheme(theme);
    listeners.forEach((listener) => listener());
};

/**
 * À appeler avant le premier rendu : applique le thème mémorisé (pas de flash clair) et suit le
 * réglage du système tant que le choix est "system".
 */
export const initTheme = () => {
    currentTheme = readStoredTheme();
    applyTheme(currentTheme);
    if (typeof window.matchMedia === 'function') {
        window.matchMedia(DARK_QUERY).addEventListener('change', () => {
            if (currentTheme === 'system') {
                applyTheme('system');
            }
        });
    }
};
